// Inline string-array editor used by Pains, Value props, Hot signals,
// banned words, question guide. Renders chips for short items + an input
// row to add new ones. The `tone` prop adapts the chip styling.

import { useState } from 'react';

import { useT } from '@barel/i18n';
import { Button, Input, Pill } from '@barel/ui';

export interface BulletListEditorProps {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  addLabel: string;
  /** Tone for the rendered chip — drives Pill `tone` prop. */
  tone?: 'accent' | 'warning' | 'danger';
  testId?: string;
}

const PILL_TONE: Record<
  NonNullable<BulletListEditorProps['tone']>,
  'accent' | 'warning' | 'error'
> = {
  accent: 'accent',
  warning: 'warning',
  danger: 'error',
};

export function BulletListEditor({
  values,
  onChange,
  placeholder,
  addLabel,
  tone = 'accent',
  testId,
}: BulletListEditorProps) {
  const tCommon = useT('common');
  const [draft, setDraft] = useState('');

  const add = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (values.includes(trimmed)) {
      setDraft('');
      return;
    }
    onChange([...values, trimmed]);
    setDraft('');
  };

  const remove = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-2" data-testid={testId ? `${testId}-list` : undefined}>
          {values.map((value, idx) => (
            <Pill
              key={`${value}-${idx}`}
              tone={PILL_TONE[tone]}
              variant="soft"
              size="sm"
              data-testid={testId ? `${testId}-chip-${idx}` : undefined}
            >
              <span>{value}</span>
              <button
                type="button"
                onClick={() => remove(idx)}
                aria-label={tCommon('actions.remove')}
                className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded text-tertiary hover:text-primary"
                data-testid={testId ? `${testId}-remove-${idx}` : undefined}
              >
                ×
              </button>
            </Pill>
          ))}
        </div>
      ) : null}
      <div className="flex items-stretch gap-2">
        <Input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={placeholder}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add();
            }
          }}
          data-testid={testId ? `${testId}-input` : undefined}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={add}
          disabled={draft.trim().length === 0}
          data-testid={testId ? `${testId}-add` : undefined}
        >
          {addLabel}
        </Button>
      </div>
    </div>
  );
}
